/**
 * Note AI Edit Hooks
 *
 * Custom hooks for managing note AI edit interactions
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { optimisticallyIncrementEntitlement } from '@features/entitlements';
import {
  notesApi,
  type PatchSectionArgs,
  useNoteWithPolling,
  useUpdateNoteSectionMutation,
} from '@features/notes';
import { useAppDispatch, useAppSelector } from '@store/hooks';

import type { NoteChatRequestPayload } from '../types';

import {
  addUserMessage,
  clearAffectedSection,
  clearInterrupt,
  clearSectionSnapshot,
} from './chatSlice';
import {
  selectAffectedSectionIds,
  selectAssistantStatus,
  selectFocusedSection,
  selectInterrupt,
  selectInterruptType,
  selectMessages,
  selectSectionUpdates,
  selectStreamingSections,
  selectThreadId,
} from './selectors';
import { useStreamNoteChatQuery } from './streamingApi';

/**
 * Generate unique run ID for each AG-UI request
 */
const generateRunId = (): string => crypto.randomUUID();

/**
 * Hook for managing note chat interactions
 *
 * Provides actions for:
 * - Sending text messages (AG-UI message format)
 * - Responding to section selection interrupt (AG-UI resume format)
 * - Responding to approval interrupt (AG-UI resume format)
 *
 * @param noteId - The note ID to interact with
 */
export const useNoteChatActions = (noteId: string) => {
  const dispatch = useAppDispatch();
  const threadId = useAppSelector(selectThreadId);
  const interrupt = useAppSelector(selectInterrupt);
  const interruptType = useAppSelector(selectInterruptType);
  const focusedSection = useAppSelector(selectFocusedSection);

  const [currentPayload, setCurrentPayload] = useState<NoteChatRequestPayload | null>(null);
  const entitlementPatchRef = useRef<{ undo: () => void } | null>(null);

  // Guard against double-click / rapid repeated submissions.
  // Uses a ref so the flag is set synchronously (no re-render gap).
  const isSubmittingRef = useRef(false);

  // Use RTK Query streaming with skip control
  const { data, isLoading, error } = useStreamNoteChatQuery(
    currentPayload as NoteChatRequestPayload,
    {
      skip: !currentPayload,
    },
  );

  // Reset submission guard when interrupt changes (new interrupt or cleared)
  useEffect(() => {
    isSubmittingRef.current = false;
  }, [interrupt?.interruptId]);

  // Rollback optimistic entitlement update on streaming error
  useEffect(() => {
    if (error && entitlementPatchRef.current) {
      entitlementPatchRef.current.undo();
      entitlementPatchRef.current = null;
    }
  }, [error]);

  // Clear entitlement ref on successful stream completion
  useEffect(() => {
    if (!isLoading && !error && data && entitlementPatchRef.current) {
      entitlementPatchRef.current = null;
    }
  }, [isLoading, error, data]);

  /**
   * Send text message (initial or continuation)
   * Uses AG-UI message format
   */
  const sendMessage = useCallback(
    (message: string) => {
      dispatch(addUserMessage(message));

      // Include focused section ID if available
      const selectedSectionIds = focusedSection ? [focusedSection.id] : undefined;

      setCurrentPayload({
        noteId,
        threadId: threadId || undefined, // Include if exists, omit on first request
        runId: generateRunId(),
        messages: [
          {
            id: crypto.randomUUID(),
            role: 'user',
            content: message,
          },
        ],
        ...(selectedSectionIds && { selected_section_ids: selectedSectionIds }),
      });
    },
    [dispatch, threadId, noteId, focusedSection],
  );

  /**
   * Respond to MULTIPLE_CHOICE or MULTI_STEP interrupt
   * Uses AG-UI resume format with selected_ids payload (for MULTIPLE_CHOICE)
   * or multi-step responses object (for MULTI_STEP)
   */
  const selectOptions = useCallback(
    (
      optionIdsOrResponses:
        | string[]
        | Record<string, { selected_ids?: string[]; confirmed?: boolean }>,
    ) => {
      if (isSubmittingRef.current) return;

      if (!threadId) {
        console.error('[AG-UI] Cannot select options: no thread ID');
        return;
      }

      const interruptId = interrupt?.interruptId;
      if (!interruptId) {
        console.error('[AG-UI] Cannot select options: no interrupt ID');
        return;
      }

      isSubmittingRef.current = true;

      // Check if this is a multi-step response object
      const isMultiStepResponse =
        interruptType === 'MULTI_STEP' &&
        typeof optionIdsOrResponses === 'object' &&
        !Array.isArray(optionIdsOrResponses);

      if (isMultiStepResponse) {
        // MULTI_STEP interrupt - use responses object directly
        dispatch(clearInterrupt());

        setCurrentPayload({
          noteId,
          threadId,
          runId: generateRunId(),
          resume: {
            interruptId,
            payload: optionIdsOrResponses,
          },
        });
        return;
      }

      // MULTIPLE_CHOICE interrupt - use selected_ids array or text
      if (interruptType !== 'MULTIPLE_CHOICE') {
        console.error('[AG-UI] Cannot select options: interrupt is not MULTIPLE_CHOICE or MULTI_STEP');
        return;
      }

      if (!Array.isArray(optionIdsOrResponses)) {
        console.error('[AG-UI] Cannot select options: expected array for MULTIPLE_CHOICE');
        return;
      }

      dispatch(clearInterrupt());

      setCurrentPayload({
        noteId,
        threadId,
        runId: generateRunId(),
        resume: {
          interruptId,
          payload: { selected_ids: optionIdsOrResponses },
        },
      });
    },
    [dispatch, threadId, interrupt, interruptType, noteId],
  );

  /**
   * Respond to MULTIPLE_CHOICE interrupt with free-text input
   * Used when allowTextInput is enabled on the interrupt
   */
  const submitTextResponse = useCallback(
    (text: string) => {
      if (isSubmittingRef.current) return;

      if (!threadId) {
        console.error('[AG-UI] Cannot submit text response: no thread ID');
        return;
      }

      const interruptId = interrupt?.interruptId;
      if (!interruptId) {
        console.error('[AG-UI] Cannot submit text response: no interrupt ID');
        return;
      }

      isSubmittingRef.current = true;

      if (interruptType !== 'MULTIPLE_CHOICE') {
        console.error('[AG-UI] Cannot submit text response: interrupt is not MULTIPLE_CHOICE');
        return;
      }

      dispatch(addUserMessage(text));
      dispatch(clearInterrupt());

      setCurrentPayload({
        noteId,
        threadId,
        runId: generateRunId(),
        resume: {
          interruptId,
          payload: { text },
        },
      });
    },
    [dispatch, threadId, interrupt, interruptType, noteId],
  );

  /**
   * Respond to CONFIRMATION interrupt (e.g., approval workflow, training decision)
   * Uses AG-UI resume format with confirmed payload
   */
  const submitConfirmation = useCallback(
    (confirmed: boolean) => {
      if (isSubmittingRef.current) return;

      if (!threadId) {
        console.error('[AG-UI] Cannot submit confirmation: no thread ID');
        return;
      }

      const interruptId = interrupt?.interruptId;
      if (!interruptId) {
        console.error('[AG-UI] Cannot submit confirmation: no interrupt ID');
        return;
      }

      isSubmittingRef.current = true;

      if (interruptType !== 'CONFIRMATION') {
        console.error('[AG-UI] Cannot submit confirmation: interrupt is not CONFIRMATION');
        return;
      }

      // Optimistically update entitlement if confirming training
      if (confirmed && interrupt?.confirmationType === 'train') {
        // Only apply optimistic update if one isn't already pending
        if (!entitlementPatchRef.current) {
          entitlementPatchRef.current = dispatch(
            optimisticallyIncrementEntitlement('in_note_training'),
          ) as unknown as { undo: () => void };
        }
        // Note: Server invalidation happens after stream completes via markdown-editor-ai mutation
      }

      // Don't clear interrupt here - let completeWorkflow() handle it after stream finishes
      // This prevents race condition where edits prop has stale data while affectedSectionIds is cleared

      setCurrentPayload({
        noteId,
        threadId,
        runId: generateRunId(),
        resume: {
          interruptId,
          payload: { confirmed },
        },
      });
    },
    [dispatch, threadId, interrupt, interruptType, noteId],
  );

  /**
   * Respond to EXPRESS_INTEREST interrupt
   * Uses AG-UI resume format with interested payload
   */
  const submitExpressInterest = useCallback(
    (interested: boolean) => {
      if (isSubmittingRef.current) return;

      if (!threadId) {
        console.error('[AG-UI] Cannot submit express interest: no thread ID');
        return;
      }

      const interruptId = interrupt?.interruptId;
      if (!interruptId) {
        console.error('[AG-UI] Cannot submit express interest: no interrupt ID');
        return;
      }

      isSubmittingRef.current = true;

      if (interruptType !== 'EXPRESS_INTEREST') {
        console.error('[AG-UI] Cannot submit express interest: interrupt is not EXPRESS_INTEREST');
        return;
      }

      dispatch(clearInterrupt());

      setCurrentPayload({
        noteId,
        threadId,
        runId: generateRunId(),
        resume: {
          interruptId,
          payload: { interested },
        },
      });
    },
    [dispatch, threadId, interrupt, interruptType, noteId],
  );

  return {
    sendMessage,
    selectOptions,
    submitTextResponse,
    submitConfirmation,
    submitExpressInterest,
    isStreaming: data?.isActive || false,
    isLoading,
    error,
  };
};

/**
 * Container hook for note chat with sections integration
 *
 * Combines:
 * - Note data fetching with polling (useNoteWithPolling)
 * - Chat streaming actions (useNoteChatActions)
 * - Section update mutations
 * - UI state management (debug panel, editing)
 *
 * This hook orchestrates all data and actions needed for the NoteChatView
 * with inline section editing and approval workflow.
 *
 * @param noteId - The note ID to interact with
 * @returns Unified interface for note chat with sections
 */
export const useNoteChatWithSections = (noteId: string) => {
  const dispatch = useAppDispatch();

  // Note data fetching with auto-polling
  const {
    data: noteData,
    isLoading: isNoteLoading,
    isError: isNoteError,
    error: noteError,
  } = useNoteWithPolling(noteId, {
    skip: !noteId,
  });

  // Chat streaming actions
  const chatActions = useNoteChatActions(noteId);

  // Section update mutation (for manual user edits)
  const [updateSection] = useUpdateNoteSectionMutation();

  // Redux selectors
  const messages = useAppSelector(selectMessages);
  const assistantStatus = useAppSelector(selectAssistantStatus);
  const interrupt = useAppSelector(selectInterrupt);
  const affectedSectionIds = useAppSelector(selectAffectedSectionIds);
  const sectionUpdates = useAppSelector(selectSectionUpdates);
  const streamingSections = useAppSelector(selectStreamingSections);

  // Local UI state
  const [editingSectionId, setEditingSectionId] = useState<string | null>(null);

  /**
   * Handle section content change from EditorView
   */
  const handleSectionChange = useCallback(
    async (sectionIndex: number, editedText: string) => {
      if (!noteId) return;

      // GUARD: Don't save if content hasn't actually changed
      // This prevents unnecessary PATCH requests during:
      // - Initial load (when BlockNote's onChange fires during initialization)
      // - User interactions that don't change content (cursor movement, selection)
      const sections = (noteData?.content?.sections || []) as Array<{
        section_id: string;
        edited_text?: string;
        text: string;
      }>;
      const section = sections[sectionIndex];
      const currentContent = section?.edited_text || section?.text || '';

      if (editedText === currentContent) {
        return;
      }

      // GUARD: Prevent auto-save during active AI streaming
      // Block saves during active streaming to prevent overwriting AI content
      const isActiveStreaming = assistantStatus === 'streaming' || streamingSections.length > 0;
      if (isActiveStreaming) {
        return;
      }

      // Get section ID to check if it's affected (reuse section from above)
      const sectionId = section?.section_id;
      const isSectionAffected = sectionId ? affectedSectionIds.includes(sectionId) : false;

      try {
        // Cast to PatchSectionArgs to include optimization flags
        const args: PatchSectionArgs = {
          noteId,
          sectionIndex,
          editSectionRequest: { edited_text: editedText },
          _skipInvalidation: true, // Skip auto-invalidation for auto-save (local editor is master)
          _skipOptimisticUpdate: true, // Skip optimistic cache update (prevents cursor jump during typing)
          _sectionId: sectionId,
        };
        await updateSection(args).unwrap();

        // Clear affected state when user edits (implicit acceptance)
        if (isSectionAffected && sectionId) {
          dispatch(clearAffectedSection(sectionId));
          dispatch(clearSectionSnapshot(sectionId));
        }
      } catch (error) {
        console.error('Failed to update section:', error);
        // On error, invalidate cache to refetch server's "last known good" state
        dispatch(notesApi.util.invalidateTags([{ type: 'Note', id: noteId }]));
        // Re-throw to let useAutosave handle error state
        throw error;
      }
    },
    [
      noteId,
      noteData,
      updateSection,
      assistantStatus,
      streamingSections,
      affectedSectionIds,
      dispatch,
    ],
  );

  /**
   * Handle approval action (approve/reject AI changes)
   * @param approved - Whether to approve or reject
   * @param skipCacheUpdate - If true, skip optimistic cache update (e.g., when user is editing)
   */
  const handleApproval = useCallback(
    (approved: boolean, skipCacheUpdate = false) => {
      // If user approves, optimistically update the note cache with AI changes
      // The backend will persist changes when it processes the confirmation
      // Skip cache update if user is actively editing (their edits take precedence)
      if (approved && Object.keys(sectionUpdates).length > 0 && !skipCacheUpdate) {
        console.debug('[Note AI Edit] 💾 Optimistically updating note sections in cache...');

        const sections = (noteData?.content?.sections || []) as Array<{ section_id: string }>;

        // Optimistically update RTK Query cache for all affected sections
        dispatch(
          notesApi.util.updateQueryData('getV2ProviderNotesByNoteId', { noteId }, draft => {
            if (!draft.content?.sections) return;

            // Apply all section updates to the cache
            Object.entries(sectionUpdates).forEach(([sectionId, content]) => {
              const sectionIndex = sections.findIndex(s => s.section_id === sectionId);

              if (sectionIndex !== -1 && draft.content.sections?.[sectionIndex]) {
                draft.content.sections[sectionIndex].edited_text = content;
              }
            });
          }),
        );

        console.debug('[Note AI Edit] ✅ Cache updated optimistically');
      } else if (approved && skipCacheUpdate) {
        console.debug('[Note AI Edit] ⏭️ Skipping cache update (user is editing)');
      } else if (!approved) {
        console.debug('[Note AI Edit] 🚫 User rejected, no cache update');
      }

      // Submit confirmation to backend - backend will persist the changes
      // Note: sectionUpdates will be cleared by completeWorkflow or when the next interrupt arrives
      chatActions.submitConfirmation(approved);
    },
    [chatActions, sectionUpdates, noteData, noteId, dispatch],
  );

  return {
    // Note data
    noteData,
    sections: noteData?.content?.sections || [],
    noteTitle: noteData?.note_title || '',
    isNoteLoading,
    isNoteError,
    noteError,

    // Chat data
    messages,
    assistantStatus,

    // Chat actions
    sendMessage: chatActions.sendMessage,
    selectOptions: chatActions.selectOptions,
    submitTextResponse: chatActions.submitTextResponse,
    submitConfirmation: chatActions.submitConfirmation,
    submitExpressInterest: chatActions.submitExpressInterest,

    // Section editing
    handleSectionChange,
    editingSectionId,
    setEditingSectionId,

    // Section streaming
    sectionUpdates,
    streamingSections,

    // Unified interrupt workflow
    interrupt,
    affectedSectionIds,
    handleApproval,
  };
};
